import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { completeMcpSsoLinkQuery, getOwnMcpSsoStatusQuery, startMcpSsoLinkQuery } from "@query";
import { Alert, Button} from "@components";
import { getApiErrorMessage } from "@client";
import { Chip, CircularProgress, Grid } from "@material-ui/core";
import { CheckCircleOutline, LinkOutlined } from "@material-ui/icons";
import { canConnectMcpSso, parseMcpSsoCallback } from "@utils";
import useStyle from "./McpSsoOwnAccess.style";

export const McpSsoOwnAccess = () => {
  const { t } = useTranslation();
  const classes = useStyle();
  
  const [ access, setAccess ] = useState();
  const [ loading, setLoading ] = useState(true);
  const [ error, setError ] = useState();
  
  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      setAccess(await getOwnMcpSsoStatusQuery());
    } catch (loadError) {
      setError(getApiErrorMessage(loadError, t("errors.network")));
    } finally {
      setLoading(false);
    }
  }, [t]);
  
  useEffect(() => {
    const completeCallback = async () => {
      const callback = parseMcpSsoCallback(window.location.search);
      if (callback.type === "none") {
        await loadStatus();
        return;
      }
      
      window.history.replaceState({}, document.title, window.location.pathname);
      if (callback.type === "error") {
        await loadStatus();
        setError(
          t(
            callback.code === "access_denied"
              ? "views.settings.mcp_sso.callback_cancelled"
              : "views.settings.mcp_sso.callback_error"
          )
        );
        return;
      }
      
      setLoading(true);
      try {
        await completeMcpSsoLinkQuery(callback.state, callback.code);
        await loadStatus();
      } catch (callbackError) {
        setLoading(false);
        setError(
          getApiErrorMessage(callbackError, t("views.settings.mcp_sso.callback_error"))
        );
      }
    };
    
    completeCallback();
  }, [loadStatus, t]);
  
  const connect = async (provider) => {
    setLoading(true);
    try {
      const response = await startMcpSsoLinkQuery(provider);
      window.location.assign(response.authorizationUrl);
    } catch (connectError) {
      setLoading(false);
      setError(
        getApiErrorMessage(connectError, t("errors.network"))
      );
    }
  };
  
  if (loading) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={24}/>
      </Grid>
    );
  }
  
  const canConnect = canConnectMcpSso(access);
  const status = access?.status || "revoked";
  const statusClass = {
    active: classes.statusActive,
    provisioning: classes.statusPending,
    awaiting_link: classes.statusPending,
    sync_error: classes.statusError,
  }[status] || classes.statusNeutral;
  const provider = {google: "Google", microsoft: "Microsoft"}[access?.linkedProvider]
    || access?.linkedProvider;
  
  return (
    <>
      <Grid
        item container
        alignItems="center"
        className="mb-4"
      >
        <h2>{t("views.settings.mcp_sso.title")}</h2>
        <Chip
          className={`${classes.status} ${statusClass}`}
          icon={status === "active" ? <CheckCircleOutline/> : undefined}
          label={t(`views.team.mcp_sso.status.${status}`)}
          size="small"
        />
      </Grid>
      <Grid
        item container
        direction="column"
        className={classes.root}
      >
        <Grid
          item container
          alignItems="center"
          justifyContent="space-between"
          wrap="nowrap"
          className={classes.title}
        >
          <Grid
            item container
            alignItems="center"
            wrap="nowrap"
          >
            <Grid item>
              <p className={classes.description}>
                {t("views.settings.mcp_sso.description")}
              </p>
            </Grid>
          </Grid>
          
        </Grid>
        
        <Grid item container spacing={3} className="my-0">
          {
            access?.accessLevel && access?.accessLevel !== "none" &&
            <Grid item xs={12} sm={4}>
              <p className={classes.label}>{t("views.settings.mcp_sso.level")}</p>
              <p
                className={`${classes.value} ${
                  access?.accessLevel ? classes.scope : ""
                }`}
              >
                {access?.accessLevel || t("views.settings.mcp_sso.level_none")}
              </p>
            </Grid>
          }
          {
            provider &&
            <Grid item xs={12} sm={4}>
              <p className={classes.label}>
                {t("views.settings.mcp_sso.provider")}
              </p>
              <p className={classes.value}>{provider}</p>
            </Grid>
          }
          {
            access?.linkedAt &&
            <Grid item xs={12} sm={4}>
              <p className={classes.label}>
                {t("views.settings.mcp_sso.connected_at")}
              </p>
              <p className={classes.value}>{access.linkedAt}</p>
            </Grid>
          }
        </Grid>
        
        {
          canConnect &&
          <Grid item container alignItems="center" className={classes.actions}>
            <Grid item container className={classes.actionButtons}>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  icon={<LinkOutlined/>}
                  onClick={() => connect("google")}
                >
                  {t("views.settings.mcp_sso.connect_google")}
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  icon={<LinkOutlined/>}
                  onClick={() => connect("microsoft")}
                >
                  {t("views.settings.mcp_sso.connect_microsoft")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        }
        {
          access?.accessLevel === "none" &&
          <p>{t("views.settings.mcp_sso.ask_admin")}</p>
        }
        <Alert
          isOpen={Boolean(error)}
          title={error}
          acceptText={t("common.close")}
          onAccept={() => setError(undefined)}
        />
      </Grid>
    </>
  );
};
